import type { ReactNode } from 'react';
import { hasPremiumFeature, type CustomField } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Input,
  NamesInput,
  Select,
  Stack,
  Switch,
  Typography,
  type NamesProblem,
} from '@src/components';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { CustomQuestionBuilder } from '@src/pages/BoxPage/full/CustomQuestionBuilder';
import { type NewBoxDraft } from './draft';
import { cashCurrencyCodes, currencyOptionLabel } from '@src/site/currency';
import './NewBoxReview.css';

export function NewBoxReview({
  draft,
  onChange,
  onEditTheme,
  onNamesProblem,
  onQuestionnaireBlocked,
  localQuestions,
  onLocalQuestionsChange,
  afterParticipants,
  premiumContext,
}: {
  afterParticipants?: ReactNode;
  premiumContext?: string;
  draft: NewBoxDraft;
  onChange: (draft: NewBoxDraft) => void;
  onEditTheme: () => void;
  onNamesProblem: (problem: NamesProblem | null) => void;
  onQuestionnaireBlocked: (blocked: boolean) => void;
  /** Edit a copy locally without saving changes to the source game. */
  localQuestions?: CustomField[];
  onLocalQuestionsChange?: (fields: CustomField[]) => void;
}) {
  const { t } = useTranslation();
  const patch = (next: Partial<NewBoxDraft>) => onChange({ ...draft, ...next });
  return (
    <Stack gap={5}>
      <Input
        label={t('newBox.name.label')}
        value={draft.name}
        maxLength={40}
        onValueChange={(name) => patch({ name })}
        testId="review-game-name"
      />
      <div className="new-box-review__appearance">
        <div className="new-box-review__picture">
          <BoxPicture picture={draft.picture} logo={draft.logo} season={draft.theme} />
        </div>
        <div>
          <Typography as="h2" role="title/card">
            {t('newBox.picture.header')}
          </Typography>
          <Typography as="p" role="meta/helper">
            {t(`newBox.appearance.${draft.theme}`)}
          </Typography>
        </div>
        <Button variant="secondary" size="small" onClick={onEditTheme} testId="review-edit-theme">
          {t('newBox.review.change')}
        </Button>
      </div>
      <Divider />
      <div className="new-box-review__organizer">
        <Input
          label={t('newBox.organizer.label')}
          value={draft.organizerName}
          maxLength={64}
          onValueChange={(organizerName) => patch({ organizerName })}
          testId="review-organizer"
        />
        <Switch
          label={t('newBox.review.participate')}
          checked={draft.participates === true}
          onCheckedChange={(participates) => patch({ participates })}
          testId="review-participate"
        />
      </div>
      <NamesInput
        rows={3}
        names={draft.participantNames}
        onNamesChange={(participantNames) => patch({ participantNames })}
        onProblem={onNamesProblem}
        label={t('newBox.people.label')}
        placeholder={t('newBox.people.placeholder')}
        hint={t('newBox.people.hint')}
        removeLabel={(name) => t('randomizer.names.remove', { name })}
        testId="review-people"
      />
      {afterParticipants}
      <Divider />
      <Switch
        label={t('newBox.review.budget')}
        checked={draft.budgetPreset !== 'none'}
        onCheckedChange={(enabled) => patch({ budgetPreset: enabled ? 'custom' : 'none' })}
      />
      {draft.budgetPreset !== 'none' && (
        <div className="new-box-review__budget">
          <Input
            type="number"
            min={0}
            max={9999999}
            label={t('newBox.cost.min')}
            value={String(draft.cashLimitMin)}
            onValueChange={(value) => patch({ cashLimitMin: Number(value) })}
          />
          <Input
            type="number"
            min={1}
            max={10000000}
            label={t('newBox.cost.max')}
            error={
              draft.cashLimit > 0 && draft.cashLimitMin > draft.cashLimit
                ? t('newBox.cost.minExceedsMax')
                : undefined
            }
            value={String(draft.cashLimit)}
            onValueChange={(value) => patch({ cashLimit: Number(value) })}
          />
          <Select
            label={t('newBox.cost.currency')}
            value={draft.cashLimitCurrency}
            options={cashCurrencyCodes().map((value) => ({
              value,
              label: currencyOptionLabel(value, t(`currency.${value}`)),
            }))}
            onValueChange={(value) =>
              patch({ cashLimitCurrency: value as NewBoxDraft['cashLimitCurrency'] })
            }
          />
        </div>
      )}
      <Switch
        label={t('game.settings.names')}
        hint={t('game.settings.namesHint')}
        checked={draft.useNames}
        onCheckedChange={(useNames) => patch({ useNames })}
      />
      <Divider />
      <CustomQuestionBuilder
        premiumContext={premiumContext}
        decorated={false}
        isPremium={hasPremiumFeature(draft, 'boxQuestions')}
        boxKey={draft.key}
        onBlockedChange={onQuestionnaireBlocked}
        demoFields={localQuestions}
        onDemoFieldsChange={onLocalQuestionsChange}
      />
      <Typography as="p" role="meta/helper">
        {t('newBox.review.questionsHint')}
      </Typography>
    </Stack>
  );
}
